import { useCustomizationStore } from '@/features/theme-editor/stores/customizationStore';
import { useThemeEditorStore } from '@/features/theme-editor/stores/themeEditorStore';
import { getColorCollection } from '@/features/theme-editor/utils/colors';
import { Colors } from '@/types/store';
import { cloneDeep, get, set } from 'lodash';
import { useState } from 'react';
import { Panel, PanelFooter } from '../../../Panel';
import { Color } from './Color';
import './react-colorful.scss';

const ColorDescription: Record<string, { label: string; description: string } | undefined> = {
    'fg.primary': {
        label: 'Primary foreground',
        description: 'Texts & icons',
    },
    'fg.secondary': {
        label: 'Secondary foreground',
        description: 'Contrast texts & labels',
    },
    'accent.primary': {
        label: 'Theme accent',
        description: 'Buttons & graphics',
    },
    'accent.secondary': {
        label: 'UI accent',
        description: 'Contrast graphics',
    },
    'fg.tertiary': {
        label: 'Border',
        description: 'Outlines',
    },
    'bg.secondary': {
        label: 'Primary background',
        description: 'Landing page',
    },
    'bg.primary': {
        label: 'Secondary background',
        description: 'Product page & sections',
    },
    'bg.tertiary': {
        label: 'Contrast background',
        description: 'Features & elements',
    },
    'fg.input': {
        label: 'Forms foreground',
        description: 'Input fields',
    },
    'bg.input': {
        label: 'Forms background',
        description: 'Input text',
    },
    'fg.overlay': {
        label: 'Overlay foreground',
        description: 'Contrasting text & graphics',
    },
    'bg.overlay': {
        label: 'Overlay background',
        description: 'Contrasting section',
    },
};

export const EditColors = () => {
    const { colors, updateStoreCustomizations } = useCustomizationStore();
    const { setCustomColors } = useThemeEditorStore();
    const [colorKey, setColorKey] = useState('');

    const updateColor = (hex: string, id: string) => {
        if (!colors) return;
        updateStoreCustomizations({ colors: set(cloneDeep(colors), id, hex) as Colors });
        const collection = getColorCollection(colors);
        if (!collection) setCustomColors({ ...colors });
    };

    const classes = 'animate-in fade-in fill-mode-backwards slide-in-from-bottom-[50px] duration-300 ease-out-circ';

    return (
        <Panel className='edit-colors w-full' footer={<PanelFooter />}>
            <div className='caption-3-regular flex flex-nowrap gap-3 rounded-md bg-neutral-50 p-3'>
                <span>
                    <span className='font-bold'>Synced colors</span> — One update refreshes all matching elements in
                    your Store.
                </span>
            </div>
            {colors && (
                <div className='flex w-full flex-col gap-2'>
                    {Object.entries(ColorDescription).map(([key, value], index) => (
                        <div key={key} className={classes} style={{ animationDelay: `${index * 15}ms` }}>
                            <Color
                                id={key}
                                label={value?.label || key}
                                description={value?.description}
                                hex={get(colors, key)}
                                onClick={id => {
                                    if (colorKey === id) setColorKey('');
                                    else setColorKey(id);
                                }}
                                active={colorKey === key}
                                onHexChange={updateColor}
                            />
                        </div>
                    ))}
                </div>
            )}
        </Panel>
    );
};
